import React, { FC, useCallback } from "react";
import { connect } from "react-redux";

import { faArrowsUpDown, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import Resource from "@mapmycustomers/shared/types/entity/Resource";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import { getPinnedResources } from "../../store";

import styles from "./PinnedResources.module.scss";
import ReorderModal from "./ReorderModal";
import ResourceCard from "./ResourceCard";

export interface PinnedResourcesProps {
  resources: Resource[];
}

const PinnedResources: FC<PinnedResourcesProps> = ({ resources }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [open, show, hide] = useBoolean();

  const handleClickReorder = useCallback(() => {
    show();
    analytics.initiated(["Reorder Pins"]);
  }, [analytics, show]);

  if (resources.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.top}>
        <div className={styles.pinned}>
          <span>
            {intl.formatMessage({
              id: "resources.pinned.title",
              defaultMessage: "Pinned",
              description: "Pinned resources title",
            })}
          </span>
          <Tooltip
            placement="right"
            title={intl.formatMessage({
              id: "resources.pinned.info.tooltip",
              defaultMessage:
                "Pinned resources display in this order on the mobile app for easy access.",
              description: "Pinned resources info tooltip",
            })}
          >
            <FontAwesomeIcon className={styles.info} icon={faInfoCircle} />
          </Tooltip>
        </div>
        <Button
          icon={<FontAwesomeIcon icon={faArrowsUpDown} />}
          onClick={handleClickReorder}
          size="small"
        >
          {intl.formatMessage({
            id: "resources.pinned.reorder",
            defaultMessage: "Reorder",
            description: "Reorder pinned resources button",
          })}
        </Button>
        <ReorderModal onHide={hide} open={open} />
      </div>
      <div className={styles.list}>
        {resources.map((resource) => (
          <ResourceCard key={resource.id} resource={resource} />
        ))}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  resources: getPinnedResources(state),
});

export default connect(mapStateToProps)(PinnedResources);
